import React, { useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CalendarDays, Users, Plus, Clock, ArrowRight, 
  Trash2, Loader2, DollarSign, UserPlus, Ticket, Bell, 
  CheckCircle, XCircle, Settings, AlertCircle, Tag, X,
  Edit, Copy, Upload, Image, CreditCard, ExternalLink, Repeat, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { formatApiError } from '../lib/apiError';
import { compressImage } from '../lib/imageCompress';
import { formatActivity } from '../lib/formatActivity';

export const StudioOwnerDashboard = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [studio, setStudio] = useState(null);
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [classPasses, setClassPasses] = useState([]);
  const [waitlistData, setWaitlistData] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Helper function to sanitize slug - handles URLs, spaces, and special characters
  const sanitizeSlug = (input) => {
    if (!input) return '';
    let slug = input;
    
    // If it looks like a URL, try to extract just the path/slug part
    if (slug.includes('://') || slug.includes('.ie') || slug.includes('.com')) {
      // Remove protocol
      slug = slug.replace(/^https?:\/\//, '');
      // Remove domain
      slug = slug.replace(/^[^/]+\//, '');
      // Remove trailing slashes
      slug = slug.replace(/\/+$/, '');
      // If there's still a path, get the last segment
      if (slug.includes('/')) {
        slug = slug.split('/').pop();
      }
    }
    
    // Convert to lowercase, replace spaces with hyphens, remove invalid chars
    slug = slug.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')  // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    return slug;
  };
  
  // Dialog states
  const [showCreateStudio, setShowCreateStudio] = useState(false);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [showEditClass, setShowEditClass] = useState(false);
  const [showCloneClass, setShowCloneClass] = useState(false);
  const [showCreateInstructor, setShowCreateInstructor] = useState(false);
  const [showCreateClassPass, setShowCreateClassPass] = useState(false);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showActivityTypeDialog, setShowActivityTypeDialog] = useState(false);
  const [showPaymentOnboarding, setShowPaymentOnboarding] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [selectedClassWaitlist, setSelectedClassWaitlist] = useState(null);
  const [selectedClassEdit, setSelectedClassEdit] = useState(null);
  const [selectedClassClone, setSelectedClassClone] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [uploadTarget, setUploadTarget] = useState({ type: '', id: '' });

  // Form states
  const [studioForm, setStudioForm] = useState({ 
    name: '', slug: '', description: '', 
    address: '', city: '', county: '', eircode: '',
    phone: '', email: '',
    category: 'fitness',
    activity_types: ['yoga', 'pilates', 'hiit', 'strength', 'cardio'],
    cancellation_notice_hours: 24, cancellation_refund_type: 'class_pass_credit', late_cancel_penalty: 0,
    // Iter-46am: per-studio waitlist priority window (0-240 min, 30-min steps).
    // Default 60 mirrors the previous global default.
    waitlist_grace_minutes: 60
  });
  const [classForm, setClassForm] = useState({ 
    name: '', description: '', start_time: '', end_time: '', max_capacity: 20, 
    price: 0, category: '', activity_type: '', instructor_id: '',
    recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
  });
  const [instructorForm, setInstructorForm] = useState({ 
    first_name: '', last_name: '', email: '', bio: '', hourly_rate: 0, specialties: '', password: ''
  });
  const [classPassForm, setClassPassForm] = useState({
    name: '', description: '', activity_type: '', passes_count: 10, 
    price: 0, validity_days: 30, transferable: false
  });
  const [newActivityType, setNewActivityType] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);

  // Iter-52: When a fresh studio_owner (no studio_id yet) lands on the
  // dashboard, we still need to release the loading state so the "Create
  // Your Studio" card renders instead of an infinite spinner. Previously
  // the effect only fired when studio_id was set — new owners were stuck.
  useEffect(() => {
    if (user?.studio_id) {
      fetchDashboardData();
    } else if (user) {
      // No studio linked yet — try to find one by owner_id, otherwise
      // release the loading state so the create-studio prompt shows.
      fetchDashboardData();
    }
    // Bug R1 fix (Iter-66): safety net — if any parallel request never
    // resolves (network hang, misconfigured proxy), release the loading
    // spinner after 15s so the user is never stuck on a blank dashboard.
    // Idempotent — fetchDashboardData's own finally already runs first
    // on the happy path.
    const stuckGuard = setTimeout(() => {
      setLoading(false);
    }, 15000);
    return () => clearTimeout(stuckGuard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.studio_id, user?.id]);

  const fetchDashboardData = async () => {
    try {
      if (user?.studio_id) {
        const [studioRes, statsRes, classesRes, instructorsRes, passesRes] = await Promise.all([
          apiGet('/api/studios').catch(() => null),
          apiGet('/api/dashboard/stats', { query: { studio_id: user.studio_id } }),
          apiGet('/api/classes', { query: { studio_id: user.studio_id } }),
          apiGet('/api/instructors', { query: { studio_id: user.studio_id } }),
          apiGet('/api/class-passes', { query: { studio_id: user.studio_id } })
        ]);

        if (Array.isArray(studioRes)) {
          const userStudio = studioRes.find(s => s.id === user.studio_id || s.owner_id === user.id);
          if (userStudio) setStudio(userStudio);
        }
        setStats(statsRes);
        setClasses(classesRes);
        setInstructors(instructorsRes);
        setClassPasses(passesRes);
      } else {
        const studiosRes = await apiGet('/api/studios');
        const userStudio = studiosRes.find(s => s.owner_id === user.id);
        if (userStudio) {
          setStudio(userStudio);
          const [statsRes, classesRes, instructorsRes, passesRes] = await Promise.all([
            apiGet('/api/dashboard/stats', { query: { studio_id: userStudio.id } }),
            apiGet('/api/classes', { query: { studio_id: userStudio.id } }),
            apiGet('/api/instructors', { query: { studio_id: userStudio.id } }),
            apiGet('/api/class-passes', { query: { studio_id: userStudio.id } })
          ]);
          setStats(statsRes);
          setClasses(classesRes);
          setInstructors(instructorsRes);
          setClassPasses(passesRes);
        }
      }
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStudio = async (e) => {
    e.preventDefault();
    
    // Validate required address fields
    if (!studioForm.address?.trim()) {
      toast.error('Street address is required');
      return;
    }
    if (!studioForm.city?.trim()) {
      toast.error('Town/City is required');
      return;
    }
    if (!studioForm.county?.trim()) {
      toast.error('County is required');
      return;
    }
    if (!studioForm.eircode?.trim()) {
      toast.error('Eircode is required');
      return;
    }
    if (!studioForm.phone?.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!studioForm.email?.trim()) {
      toast.error('Email address is required');
      return;
    }
    
    setFormLoading(true);
    try {
      const data = await apiPost('/api/studios', { body: studioForm });
      setStudio(data);
      setShowCreateStudio(false);
      toast.success('Studio created successfully!');
      // Bug #4 fix: refresh user so studio_id / role propagate before the
      // next fetchDashboardData / StudioSettingsPage load. Without this
      // /studios/me can still 404 in the settings tab even though the
      // studio was just created, because the local user snapshot is stale.
      if (refreshUser) {
        try { await refreshUser(); } catch (_) { /* non-fatal */ }
      }
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create studio'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!classForm.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    
    // If recurring, use the recurring endpoint
    if (classForm.recurring && classForm.recurrence_rule) {
      return handleCreateRecurringClass(e);
    }
    
    setFormLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await apiPost('/api/classes', { body: {
        ...classForm,
        studio_id: studio.id,
        category: classForm.activity_type,
        start_time: `${dateStr}T${classForm.start_time}:00`,
        end_time: `${dateStr}T${classForm.end_time}:00`,
        instructor_id: classForm.instructor_id || null
      } });
      setShowCreateClass(false);
      setClassForm({ 
        name: '', description: '', start_time: '', end_time: '', max_capacity: 20, 
        price: 0, category: '', activity_type: '', instructor_id: '',
        recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
      });
      toast.success('Class created successfully!');
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateInstructor = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await apiPost('/api/instructors', { body: {
        ...instructorForm,
        studio_id: studio.id,
        specialties: instructorForm.specialties.split(',').map(s => s.trim()).filter(Boolean),
        password: instructorForm.password || null
      } });
      setShowCreateInstructor(false);
      setInstructorForm({ first_name: '', last_name: '', email: '', bio: '', hourly_rate: 0, specialties: '', password: '' });
      toast.success('Instructor added successfully!');
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to add instructor'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateClassPass = async (e) => {
    e.preventDefault();
    if (!classPassForm.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    setFormLoading(true);
    try {
      await apiPost('/api/class-passes', { body: {
        ...classPassForm,
        studio_id: studio.id
      } });
      setShowCreateClassPass(false);
      setClassPassForm({ name: '', description: '', activity_type: '', passes_count: 10, price: 0, validity_days: 30, transferable: false });
      toast.success('ClassPass created!');
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create ClassPass'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddActivityType = async () => {
    if (!newActivityType.trim()) return;
    try {
      const data = await apiPost('/api/studios/{studio_id}/activity-types', { params: { studio_id: studio.id }, body: { name: newActivityType.trim() } });
      setStudio({ ...studio, activity_types: data.activity_types });
      setNewActivityType('');
      toast.success('Activity type added!');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to add activity type'));
    }
  };

  const handleRemoveActivityType = async (activityType) => {
    try {
      const data = await apiDelete('/api/studios/{studio_id}/activity-types/{activity_name}', { params: { studio_id: studio.id, activity_name: activityType } });
      setStudio({ ...studio, activity_types: data.activity_types });
      toast.success('Activity type removed!');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to remove activity type'));
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await apiDelete('/api/classes/{class_id}', { params: { class_id: classId } });
      toast.success('Class deleted');
      fetchDashboardData();
    } catch (e) {
      toast.error('Failed to delete class');
    }
  };

  const handleEditClass = (classItem) => {
    setSelectedClassEdit(classItem);
    setClassForm({
      name: classItem.name,
      description: classItem.description || '',
      start_time: classItem.start_time ? classItem.start_time.split('T')[1]?.substring(0, 5) : '',
      end_time: classItem.end_time ? classItem.end_time.split('T')[1]?.substring(0, 5) : '',
      max_capacity: classItem.max_capacity,
      price: classItem.price,
      activity_type: classItem.activity_type || '',
      instructor_id: classItem.instructor_id || '',
      recurring: false,
      recurrence_rule: '',
      recurrence_end_date: '',
      recurrence_days: []
    });
    if (classItem.start_time) {
      setSelectedDate(new Date(classItem.start_time));
    }
    setShowEditClass(true);
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await apiPut('/api/classes/{class_id}', { params: { class_id: selectedClassEdit.id }, body: {
        name: classForm.name,
        description: classForm.description,
        start_time: `${dateStr}T${classForm.start_time}:00`,
        end_time: `${dateStr}T${classForm.end_time}:00`,
        max_capacity: classForm.max_capacity,
        price: classForm.price,
        activity_type: classForm.activity_type,
        instructor_id: classForm.instructor_id || null
      } });
      setShowEditClass(false);
      setSelectedClassEdit(null);
      toast.success('Class updated!');
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenClone = (classItem) => {
    setSelectedClassClone(classItem);
    setClassForm({
      name: `${classItem.name} (Copy)`,
      description: classItem.description || '',
      start_time: classItem.start_time ? classItem.start_time.split('T')[1]?.substring(0, 5) : '',
      end_time: classItem.end_time ? classItem.end_time.split('T')[1]?.substring(0, 5) : '',
      max_capacity: classItem.max_capacity,
      price: classItem.price,
      activity_type: classItem.activity_type || '',
      instructor_id: classItem.instructor_id || '',
      recurring: false,
      recurrence_rule: '',
      recurrence_end_date: '',
      recurrence_days: []
    });
    setSelectedDate(new Date());
    setRecurrenceEndDate(null);
    setShowCloneClass(true);
  };

  const handleCloneClass = async (e) => {
    e.preventDefault();
    if (!classForm.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    setFormLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const payload = {
        ...classForm,
        studio_id: studio.id,
        start_time: `${dateStr}T${classForm.start_time}:00`,
        end_time: `${dateStr}T${classForm.end_time}:00`,
        instructor_id: classForm.instructor_id || null,
        recurrence_end_date: recurrenceEndDate ? format(recurrenceEndDate, "yyyy-MM-dd'T'23:59:59") : null
      };
      
      const data = await apiPost('/api/classes/{class_id}/clone', { params: { class_id: selectedClassClone.id }, body: payload });
      setShowCloneClass(false);
      setSelectedClassClone(null);
      toast.success(data.message || 'Class(es) created!');
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to clone class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateRecurringClass = async (e) => {
    e.preventDefault();
    if (!classForm.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    setFormLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const payload = {
        ...classForm,
        studio_id: studio.id,
        category: classForm.activity_type,
        start_time: `${dateStr}T${classForm.start_time}:00`,
        end_time: `${dateStr}T${classForm.end_time}:00`,
        instructor_id: classForm.instructor_id || null,
        recurrence_end_date: recurrenceEndDate ? format(recurrenceEndDate, "yyyy-MM-dd'T'23:59:59") : null
      };
      
      const data = await apiPost('/api/classes/recurring', { body: payload });
      setShowCreateClass(false);
      setClassForm({ 
        name: '', description: '', start_time: '', end_time: '', max_capacity: 20, 
        price: 0, category: '', activity_type: '', instructor_id: '',
        recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
      });
      setRecurrenceEndDate(null);
      toast.success(data.message || 'Class(es) created!');
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setFormLoading(true);
    try {
      // Bug R20 fix: compress client-side to stay under ingress limit.
      let uploadFile = file;
      try { uploadFile = await compressImage(file); } catch (_) { /* fall through */ }
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('entity_type', uploadTarget.type);
      formData.append('entity_id', uploadTarget.id);
      
      const data = await apiPost('/api/upload/image', { body: formData });
      
      toast.success('Photo uploaded!');
      setShowPhotoUpload(false);
      fetchDashboardData();
    } catch (e) {
      if (e?.status === 413) {
        toast.error('That image is too large. Try one under 2MB or a smaller resolution.');
      } else {
        toast.error(formatApiError(e, 'Failed to upload photo'));
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setFormLoading(true);
    try {
      // First create sub-account if not exists
      let status = onboardingStatus;
      if (!status || status.status === 'not_started') {
        await apiPost('/api/payments/onboarding/create-sub-account', { body: {
          business_name: studio.name,
          business_type: 'individual',
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name
        } });
      }
      
      // Generate onboarding link
      const data = await apiPost('/api/payments/onboarding/generate-link');
      
      // Open link in new tab
      window.open(data.url, '_blank');
      toast.success('Redirecting to RyftPay onboarding...');
      
      // Refresh status
      fetchOnboardingStatus();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to start onboarding'));
    } finally {
      setFormLoading(false);
    }
  };

  const fetchOnboardingStatus = async () => {
    try {
      const data = await apiGet('/api/payments/onboarding/status');
      setOnboardingStatus(data);
    } catch (e) {
      console.error('Failed to fetch onboarding status:', e);
    }
  };

  // Fetch onboarding status when studio is loaded
  useEffect(() => {
    if (studio) {
      fetchOnboardingStatus();
    }
  }, [studio]);

  const handleDeleteClassPass = async (passId) => {
    if (!window.confirm('Are you sure you want to deactivate this ClassPass?')) return;
    try {
      await apiDelete('/api/class-passes/{pass_id}', { params: { pass_id: passId } });
      toast.success('ClassPass deactivated');
      fetchDashboardData();
    } catch (e) {
      toast.error('Failed to deactivate ClassPass');
    }
  };

  const handleViewWaitlist = async (classItem) => {
    try {
      const data = await apiGet('/api/waitlist/class/{class_id}', { params: { class_id: classItem.id } });
      setWaitlistData(data);
      setSelectedClassWaitlist(classItem);
      setShowWaitlistDialog(true);
    } catch (e) {
      toast.error('Failed to load waitlist');
    }
  };

  const handlePromoteFromWaitlist = async (waitlistId) => {
    try {
      await apiPost('/api/waitlist/{waitlist_id}/promote', { params: { waitlist_id: waitlistId } });
      toast.success('User promoted from waitlist!');
      if (selectedClassWaitlist) {
        const data = await apiGet('/api/waitlist/class/{class_id}', { params: { class_id: selectedClassWaitlist.id } });
        setWaitlistData(data);
      }
      fetchDashboardData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to promote user'));
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await apiPut('/api/studios/{studio_id}', { params: { studio_id: studio.id }, body: {
        ...studio,
        cancellation_notice_hours: studioForm.cancellation_notice_hours,
        cancellation_refund_type: studioForm.cancellation_refund_type,
        late_cancel_penalty: studioForm.late_cancel_penalty
      } });
      toast.success('Settings updated!');
      setShowSettingsDialog(false);
      fetchDashboardData();
    } catch (e) {
      toast.error('Failed to update settings');
    } finally {
      setFormLoading(false);
    }
  };

  // Get activity types from studio
  const activityTypes = studio?.activity_types || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // Iter-72: Convert Create-Studio from a modal Dialog to a full inline
  // page rendered directly inside DashboardLayout. Modal-scroll issues
  // (viewport-clipping past a certain height) meant the last fields +
  // submit button could become unreachable on some browsers/zoom levels.
  // Rendering inline lets the browser's own page scroll handle the form,
  // which always works.
  if (!studio) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto py-6 px-2 sm:px-4">
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-1">Create Your Studio</h1>
            <p className="text-muted-foreground">Get started by setting up your fitness studio.</p>
          </div>
          <Card data-testid="create-studio-page">
            <CardContent className="pt-6">
              <form onSubmit={handleCreateStudio} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Studio Name</Label>
                    <Input
                      value={studioForm.name}
                      onChange={(e) => setStudioForm({ ...studioForm, name: e.target.value })}
                      placeholder="FlowFit Yoga"
                      required
                      data-testid="studio-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>URL Slug</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>This will be your studio&apos;s unique URL on our platform:</p>
                            <p className="font-mono text-xs mt-1 text-primary">
                              studioscheduler.ie/{studioForm.slug || 'your-studio-name'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      value={studioForm.slug}
                      onChange={(e) => setStudioForm({ ...studioForm, slug: sanitizeSlug(e.target.value) })}
                      placeholder="flowfit-yoga"
                      required
                      data-testid="studio-slug-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your studio URL: <span className="font-mono">studioscheduler.ie/{studioForm.slug || 'your-studio-name'}</span>
                    </p>
                    <p className="text-xs text-amber-600">
                      Only letters, numbers, and hyphens allowed. URLs will be automatically cleaned.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={studioForm.category} onValueChange={(v) => setStudioForm({ ...studioForm, category: v })}>
                    <SelectTrigger data-testid="studio-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yoga">Yoga Studio</SelectItem>
                      <SelectItem value="pilates">Pilates Studio</SelectItem>
                      <SelectItem value="crossfit">CrossFit Box</SelectItem>
                      <SelectItem value="gym">Gym</SelectItem>
                      <SelectItem value="fitness">Fitness Center</SelectItem>
                      <SelectItem value="dance">Dance Studio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={studioForm.description}
                    onChange={(e) => setStudioForm({ ...studioForm, description: e.target.value })}
                    placeholder="A welcoming space for all fitness levels..."
                    data-testid="studio-description-input"
                  />
                </div>

                <div className="space-y-4 pt-2">
                  <h4 className="font-medium text-sm">Studio Address <span className="text-destructive">*</span></h4>
                  <div className="space-y-2">
                    <Label>Street Address <span className="text-destructive">*</span></Label>
                    <Input
                      value={studioForm.address}
                      onChange={(e) => setStudioForm({ ...studioForm, address: e.target.value })}
                      placeholder="123 Main Street"
                      required
                      data-testid="studio-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Town/City <span className="text-destructive">*</span></Label>
                      <Input
                        value={studioForm.city}
                        onChange={(e) => setStudioForm({ ...studioForm, city: e.target.value })}
                        placeholder="Dublin"
                        required
                        data-testid="studio-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>County <span className="text-destructive">*</span></Label>
                      <Select value={studioForm.county || ''} onValueChange={(v) => setStudioForm({ ...studioForm, county: v })}>
                        <SelectTrigger data-testid="studio-county-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="carlow">Carlow</SelectItem>
                          <SelectItem value="cavan">Cavan</SelectItem>
                          <SelectItem value="clare">Clare</SelectItem>
                          <SelectItem value="cork">Cork</SelectItem>
                          <SelectItem value="donegal">Donegal</SelectItem>
                          <SelectItem value="dublin">Dublin</SelectItem>
                          <SelectItem value="galway">Galway</SelectItem>
                          <SelectItem value="kerry">Kerry</SelectItem>
                          <SelectItem value="kildare">Kildare</SelectItem>
                          <SelectItem value="kilkenny">Kilkenny</SelectItem>
                          <SelectItem value="laois">Laois</SelectItem>
                          <SelectItem value="leitrim">Leitrim</SelectItem>
                          <SelectItem value="limerick">Limerick</SelectItem>
                          <SelectItem value="longford">Longford</SelectItem>
                          <SelectItem value="louth">Louth</SelectItem>
                          <SelectItem value="mayo">Mayo</SelectItem>
                          <SelectItem value="meath">Meath</SelectItem>
                          <SelectItem value="monaghan">Monaghan</SelectItem>
                          <SelectItem value="offaly">Offaly</SelectItem>
                          <SelectItem value="roscommon">Roscommon</SelectItem>
                          <SelectItem value="sligo">Sligo</SelectItem>
                          <SelectItem value="tipperary">Tipperary</SelectItem>
                          <SelectItem value="waterford">Waterford</SelectItem>
                          <SelectItem value="westmeath">Westmeath</SelectItem>
                          <SelectItem value="wexford">Wexford</SelectItem>
                          <SelectItem value="wicklow">Wicklow</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Eircode <span className="text-destructive">*</span></Label>
                      <Input
                        value={studioForm.eircode}
                        onChange={(e) => setStudioForm({ ...studioForm, eircode: e.target.value.toUpperCase() })}
                        placeholder="D01 AB12"
                        maxLength={8}
                        required
                        data-testid="studio-eircode-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Phone <span className="text-destructive">*</span></Label>
                      <Input
                        value={studioForm.phone}
                        onChange={(e) => setStudioForm({ ...studioForm, phone: e.target.value })}
                        placeholder="+353 1 234 5678"
                        required
                        data-testid="studio-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email <span className="text-destructive">*</span></Label>
                      <Input
                        type="email"
                        value={studioForm.email}
                        onChange={(e) => setStudioForm({ ...studioForm, email: e.target.value })}
                        placeholder="info@yourstudio.ie"
                        required
                        data-testid="studio-email-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Activity Types (you can add more later)</Label>
                  <div className="flex flex-wrap gap-2">
                    {studioForm.activity_types.map((type, i) => (
                      <Badge key={`act-${type}`} variant="secondary" className="flex items-center gap-1">
                        {type}
                        <button
                          type="button"
                          onClick={() => setStudioForm({
                            ...studioForm,
                            activity_types: studioForm.activity_types.filter((_, idx) => idx !== i)
                          })}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Cancellation Policy
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Notice Required (hours)</Label>
                      <Input
                        type="number"
                        value={studioForm.cancellation_notice_hours}
                        onChange={(e) => setStudioForm({ ...studioForm, cancellation_notice_hours: parseInt(e.target.value) })}
                        min={0}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Refund Type</Label>
                      <Select
                        value={studioForm.cancellation_refund_type}
                        onValueChange={(v) => setStudioForm({ ...studioForm, cancellation_refund_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="class_pass_credit">ClassPass Credit</SelectItem>
                          <SelectItem value="full_refund">Full Refund</SelectItem>
                          <SelectItem value="no_refund">No Refund</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2 mt-4">
                    <Label>Waitlist Priority Window</Label>
                    <Select
                      value={String(studioForm.waitlist_grace_minutes ?? 60)}
                      onValueChange={(v) => setStudioForm({ ...studioForm, waitlist_grace_minutes: parseInt(v, 10) })}
                    >
                      <SelectTrigger data-testid="onboarding-waitlist-grace-select">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 minutes (no priority — open to public immediately)</SelectItem>
                        {Array.from({ length: 8 }, (_, i) => (i + 1) * 30).map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {m < 60 ? `${m} minutes` : m === 60 ? '1 hour' : m % 60 === 0 ? `${m / 60} hours` : `${Math.floor(m / 60)}h ${m % 60}m`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      When a client cancels, waitlisted members are notified. During this window only they can book the freed seat. Set 0 to skip — emails still send, public can book immediately. (You can change this any time in Settings → Cancellation Policy.)
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="submit" size="lg" disabled={formLoading} data-testid="studio-submit-btn">
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Studio'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header - Removed action buttons */}
        <div>
          <h1 className="text-3xl font-bold mb-1">{studio.name}</h1>
          <p className="text-muted-foreground">Manage your studio, classes, and ClassPasses</p>
        </div>

        {/* Activity Types Overview */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                Activity Types
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowActivityTypeDialog(true)}>
                Manage
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {activityTypes.length > 0 ? (
                activityTypes.map((type, i) => (
                  <Badge key={`at-${type}`} variant="outline" className="capitalize">{type}</Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No activity types defined. Add some to create classes and ClassPasses.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid — Iter-53b: 2-col on mobile / tablet, 4-col on desktop. */}
        <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
          <Card data-testid="stat-classes">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Classes</CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_classes || 0}</div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-bookings">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bookings</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_bookings || 0}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-classpasses">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">ClassPasses</CardTitle>
              <Ticket className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{classPasses.length}</div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-revenue">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">€{stats?.total_revenue?.toFixed(2) || '0.00'}</div>
            </CardContent>
          </Card>
        </div>

        {/* Scheduled Classes - No tabs, just show classes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Scheduled Classes</CardTitle>
            <CardDescription>Your upcoming classes</CardDescription>
          </CardHeader>
          <CardContent>
            {classes.length > 0 ? (
              <div className="space-y-3">
                {classes.slice(0, 5).map((cls, i) => (
                  <div key={cls.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`class-row-${i}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{formatActivity(cls.activity_type || cls.category) || 'Class'}</Badge>
                        <span className="font-medium">{cls.name}</span>
                        {(cls.waitlist_count || 0) > 0 && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Bell className="w-3 h-3" />
                            {cls.waitlist_count} waitlist
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(cls.start_time), 'EEE, MMM d • h:mm a')} • 
                        {cls.current_bookings}/{cls.max_capacity} booked
                        {cls.instructor_name && ` • ${cls.instructor_name}`}
                        {cls.price > 0 && ` • €${cls.price}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {(cls.waitlist_count || 0) > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewWaitlist(cls)}
                          data-testid={`view-waitlist-${i}`}
                        >
                          <Bell className="w-4 h-4 mr-1" />
                          Waitlist
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {classes.length > 5 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{classes.length - 5} more classes. <a href="/studio/classes" className="text-primary hover:underline">View all</a>
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No classes scheduled yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity Type Management Dialog */}
      <Dialog open={showActivityTypeDialog} onOpenChange={setShowActivityTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Manage Activity Types
            </DialogTitle>
            <DialogDescription>
              Activity types define what kinds of classes your studio offers. ClassPasses are linked to specific activity types.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Add new activity type */}
            <div className="flex gap-2">
              <Input 
                value={newActivityType}
                onChange={(e) => setNewActivityType(e.target.value)}
                placeholder="e.g., spinning, barre, boxing"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddActivityType())}
                data-testid="new-activity-input"
              />
              <Button onClick={handleAddActivityType} data-testid="add-activity-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Existing activity types */}
            <div className="space-y-2">
              <Label>Current Activity Types</Label>
              {activityTypes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {activityTypes.map((type, i) => (
                    <Badge 
                      key={`at-edit-${type}`} 
                      variant="secondary" 
                      className="flex items-center gap-1 capitalize pr-1"
                    >
                      {type}
                      <button
                        onClick={() => handleRemoveActivityType(type)}
                        className="ml-1 p-0.5 rounded hover:bg-destructive/20 hover:text-destructive"
                        data-testid={`remove-activity-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No activity types yet. Add some above.</p>
              )}
            </div>

            <div className="pt-4 border-t text-sm text-muted-foreground">
              <p><strong>Note:</strong> You cannot remove an activity type that is being used by classes or ClassPasses.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowActivityTypeDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Class Dialog */}
      <Dialog open={showCreateClass} onOpenChange={setShowCreateClass}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Class</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClass} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Class Name</Label>
                <Input 
                  value={classForm.name}
                  onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                  placeholder="Morning Flow"
                  required
                  data-testid="class-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Activity Type *</Label>
                <Select 
                  value={classForm.activity_type} 
                  onValueChange={(v) => setClassForm({ ...classForm, activity_type: v, category: v })}
                >
                  <SelectTrigger data-testid="class-activity-select">
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityTypes.map((type, i) => (
                      <SelectItem key={i} value={type} className="capitalize">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {format(selectedDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input 
                  type="time"
                  value={classForm.start_time}
                  onChange={(e) => setClassForm({ ...classForm, start_time: e.target.value })}
                  required
                  data-testid="class-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input 
                  type="time"
                  value={classForm.end_time}
                  onChange={(e) => setClassForm({ ...classForm, end_time: e.target.value })}
                  required
                  data-testid="class-end-time"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Capacity</Label>
                <Input 
                  type="number"
                  value={classForm.max_capacity}
                  onChange={(e) => setClassForm({ ...classForm, max_capacity: parseInt(e.target.value) })}
                  min={1}
                  data-testid="class-capacity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Drop-in Price (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={classForm.price}
                  onChange={(e) => setClassForm({ ...classForm, price: parseFloat(e.target.value) })}
                  min={0}
                  data-testid="class-price-input"
                />
                <p className="text-xs text-muted-foreground">For clients without ClassPass</p>
              </div>
            </div>

            {instructors.length > 0 && (
              <div className="space-y-2">
                <Label>Instructor (Optional)</Label>
                <Select value={classForm.instructor_id} onValueChange={(v) => setClassForm({ ...classForm, instructor_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select instructor" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((inst) => (
                      <SelectItem key={inst.id} value={inst.id}>{inst.first_name} {inst.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recurring Schedule Section */}
            <div className="pt-4 border-t space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="newClassRecurring"
                  checked={classForm.recurring}
                  onChange={(e) => setClassForm({ ...classForm, recurring: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="newClassRecurring" className="text-sm font-medium flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  Create recurring schedule
                </label>
              </div>

              {classForm.recurring && (
                <>
                  <div className="space-y-2">
                    <Label>Recurrence Pattern</Label>
                    <Select 
                      value={classForm.recurrence_rule} 
                      onValueChange={(v) => setClassForm({ ...classForm, recurrence_rule: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pattern" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly (same day each week)</SelectItem>
                        <SelectItem value="monthly">Monthly (same date each month)</SelectItem>
                        <SelectItem value="custom">Custom (select specific days)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {classForm.recurrence_rule === 'custom' && (
                    <div className="space-y-2">
                      <Label>Select Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                          <Button
                            key={day}
                            type="button"
                            variant={classForm.recurrence_days.includes(i) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const days = classForm.recurrence_days.includes(i)
                                ? classForm.recurrence_days.filter(d => d !== i)
                                : [...classForm.recurrence_days, i];
                              setClassForm({ ...classForm, recurrence_days: days });
                            }}
                          >
                            {day}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>End Recurrence On</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {recurrenceEndDate ? format(recurrenceEndDate, 'PPP') : 'Pick end date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={recurrenceEndDate}
                          onSelect={setRecurrenceEndDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateClass(false)}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={formLoading || !classForm.activity_type || (classForm.recurring && !classForm.recurrence_rule)} 
                data-testid="class-submit-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : classForm.recurring ? 'Create Recurring Classes' : 'Create Class'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create ClassPass Dialog */}
      <Dialog open={showCreateClassPass} onOpenChange={setShowCreateClassPass}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create ClassPass</DialogTitle>
            <DialogDescription>
              Create a class pass that clients can purchase to attend classes of a specific activity type.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateClassPass} className="space-y-4">
            <div className="space-y-2">
              <Label>ClassPass Name</Label>
              <Input 
                value={classPassForm.name}
                onChange={(e) => setClassPassForm({ ...classPassForm, name: e.target.value })}
                placeholder="e.g., 10-Class Yoga Pass"
                required
                data-testid="classpass-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Activity Type *</Label>
                <Select 
                  value={classPassForm.activity_type} 
                  onValueChange={(v) => setClassPassForm({ ...classPassForm, activity_type: v })}
                >
                  <SelectTrigger data-testid="classpass-activity-select">
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityTypes.map((type, i) => (
                      <SelectItem key={i} value={type} className="capitalize">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Number of Classes</Label>
                <Input 
                  type="number"
                  value={classPassForm.passes_count}
                  onChange={(e) => setClassPassForm({ ...classPassForm, passes_count: parseInt(e.target.value) })}
                  min={1}
                  required
                  data-testid="classpass-count-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={classPassForm.price}
                  onChange={(e) => setClassPassForm({ ...classPassForm, price: parseFloat(e.target.value) })}
                  min={0}
                  required
                  data-testid="classpass-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Valid for (days)</Label>
                <Input 
                  type="number"
                  value={classPassForm.validity_days}
                  onChange={(e) => setClassPassForm({ ...classPassForm, validity_days: parseInt(e.target.value) })}
                  min={1}
                  required
                  data-testid="classpass-validity-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={classPassForm.description}
                onChange={(e) => setClassPassForm({ ...classPassForm, description: e.target.value })}
                placeholder="Perfect for yoga enthusiasts who want flexibility..."
                data-testid="classpass-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateClassPass(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !classPassForm.activity_type} data-testid="classpass-submit-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create ClassPass'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Instructor Dialog */}
      <Dialog open={showCreateInstructor} onOpenChange={setShowCreateInstructor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Instructor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInstructor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input 
                  value={instructorForm.first_name}
                  onChange={(e) => setInstructorForm({ ...instructorForm, first_name: e.target.value })}
                  required
                  data-testid="instructor-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input 
                  value={instructorForm.last_name}
                  onChange={(e) => setInstructorForm({ ...instructorForm, last_name: e.target.value })}
                  required
                  data-testid="instructor-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={instructorForm.email}
                onChange={(e) => setInstructorForm({ ...instructorForm, email: e.target.value })}
                required
                data-testid="instructor-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Specialties (comma-separated)</Label>
              <Input 
                value={instructorForm.specialties}
                onChange={(e) => setInstructorForm({ ...instructorForm, specialties: e.target.value })}
                placeholder="Yoga, Pilates, HIIT"
                data-testid="instructor-specialties-input"
              />
            </div>
            <div className="pt-4 border-t space-y-2">
              <Label>Instructor Login (Optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Set a password to allow the instructor to log in and view their schedule
              </p>
              <Input 
                type="password"
                value={instructorForm.password}
                onChange={(e) => setInstructorForm({ ...instructorForm, password: e.target.value })}
                placeholder="Leave blank if no login needed"
                data-testid="instructor-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateInstructor(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="instructor-submit-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Instructor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Waitlist Management Dialog */}
      <Dialog open={showWaitlistDialog} onOpenChange={setShowWaitlistDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Waitlist Management</DialogTitle>
            <DialogDescription>
              {selectedClassWaitlist?.name} - {waitlistData.waitlist_count || 0} people waiting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {waitlistData.entries?.length > 0 ? (
              <div className="space-y-2">
                {waitlistData.entries.map((entry, i) => (
                  <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">#{entry.position}</Badge>
                        <span className="font-medium">{entry.user_name}</span>
                        {entry.status === 'promoted' && (
                          <Badge className="bg-emerald-500">Promoted</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{entry.user_email}</p>
                      <p className="text-xs text-muted-foreground">
                        Joined: {format(new Date(entry.joined_at), 'MMM d, h:mm a')}
                      </p>
                    </div>
                    {entry.status === 'waiting' && (
                      <Button 
                        size="sm" 
                        onClick={() => handlePromoteFromWaitlist(entry.id)}
                        data-testid={`promote-btn-${i}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Promote
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">No one on the waitlist</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWaitlistDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancellation Policy Settings</DialogTitle>
            <DialogDescription>
              Configure how cancellations are handled at your studio
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSettings} className="space-y-4">
            <div className="space-y-2">
              <Label>Minimum Notice Required (hours)</Label>
              <Input 
                type="number"
                value={studioForm.cancellation_notice_hours}
                onChange={(e) => setStudioForm({ ...studioForm, cancellation_notice_hours: parseInt(e.target.value) })}
                min={0}
              />
              <p className="text-xs text-muted-foreground">
                Clients must cancel this many hours before class to receive a ClassPass credit refund
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Refund Type</Label>
              <Select 
                value={studioForm.cancellation_refund_type} 
                onValueChange={(v) => setStudioForm({ ...studioForm, cancellation_refund_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="class_pass_credit">ClassPass Credit (returns pass to user)</SelectItem>
                  <SelectItem value="full_refund">Full Refund (returns payment)</SelectItem>
                  <SelectItem value="no_refund">No Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog open={showEditClass} onOpenChange={setShowEditClass}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Class</DialogTitle>
            <DialogDescription>Update class details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateClass} className="space-y-4">
            <div className="space-y-2">
              <Label>Class Name</Label>
              <Input 
                value={classForm.name}
                onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                required
                data-testid="edit-class-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Activity Type *</Label>
              <Select 
                value={classForm.activity_type} 
                onValueChange={(v) => setClassForm({ ...classForm, activity_type: v })}
              >
                <SelectTrigger data-testid="edit-class-activity">
                  <SelectValue placeholder="Select activity" />
                </SelectTrigger>
                <SelectContent>
                  {activityTypes.map((type, i) => (
                    <SelectItem key={i} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input 
                  type="time"
                  value={classForm.start_time}
                  onChange={(e) => setClassForm({ ...classForm, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input 
                  type="time"
                  value={classForm.end_time}
                  onChange={(e) => setClassForm({ ...classForm, end_time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Capacity</Label>
                <Input 
                  type="number"
                  value={classForm.max_capacity}
                  onChange={(e) => setClassForm({ ...classForm, max_capacity: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>Drop-in Price (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={classForm.price}
                  onChange={(e) => setClassForm({ ...classForm, price: parseFloat(e.target.value) })}
                  min={0}
                />
              </div>
            </div>

            {instructors.length > 0 && (
              <div className="space-y-2">
                <Label>Instructor</Label>
                <Select value={classForm.instructor_id} onValueChange={(v) => setClassForm({ ...classForm, instructor_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select instructor" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((inst) => (
                      <SelectItem key={inst.id} value={inst.id}>{inst.first_name} {inst.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditClass(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="edit-class-submit">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clone Class Dialog */}
      <Dialog open={showCloneClass} onOpenChange={setShowCloneClass}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Clone Class
            </DialogTitle>
            <DialogDescription>
              Create a copy of this class with optional recurring schedule
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCloneClass} className="space-y-4">
            <div className="space-y-2">
              <Label>Class Name</Label>
              <Input 
                value={classForm.name}
                onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                required
                data-testid="clone-class-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Activity Type *</Label>
              <Select 
                value={classForm.activity_type} 
                onValueChange={(v) => setClassForm({ ...classForm, activity_type: v })}
              >
                <SelectTrigger data-testid="clone-class-activity">
                  <SelectValue placeholder="Select activity" />
                </SelectTrigger>
                <SelectContent>
                  {activityTypes.map((type, i) => (
                    <SelectItem key={i} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input 
                  type="time"
                  value={classForm.start_time}
                  onChange={(e) => setClassForm({ ...classForm, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input 
                  type="time"
                  value={classForm.end_time}
                  onChange={(e) => setClassForm({ ...classForm, end_time: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Recurring Schedule Section */}
            <div className="pt-4 border-t space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={classForm.recurring}
                  onChange={(e) => setClassForm({ ...classForm, recurring: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="recurring" className="text-sm font-medium flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  Create recurring schedule
                </label>
              </div>

              {classForm.recurring && (
                <>
                  <div className="space-y-2">
                    <Label>Recurrence Pattern</Label>
                    <Select 
                      value={classForm.recurrence_rule} 
                      onValueChange={(v) => setClassForm({ ...classForm, recurrence_rule: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pattern" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly (same day each week)</SelectItem>
                        <SelectItem value="monthly">Monthly (same date each month)</SelectItem>
                        <SelectItem value="custom">Custom (select specific days)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {classForm.recurrence_rule === 'custom' && (
                    <div className="space-y-2">
                      <Label>Select Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                          <Button
                            key={day}
                            type="button"
                            variant={classForm.recurrence_days.includes(i) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const days = classForm.recurrence_days.includes(i)
                                ? classForm.recurrence_days.filter(d => d !== i)
                                : [...classForm.recurrence_days, i];
                              setClassForm({ ...classForm, recurrence_days: days });
                            }}
                          >
                            {day}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>End Recurrence On</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {recurrenceEndDate ? format(recurrenceEndDate, 'PPP') : 'Pick end date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={recurrenceEndDate}
                          onSelect={setRecurrenceEndDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCloneClass(false)}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={formLoading || !classForm.activity_type || (classForm.recurring && !classForm.recurrence_rule)}
                data-testid="clone-class-submit"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : classForm.recurring ? 'Create Recurring Classes' : 'Clone Class'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={showPhotoUpload} onOpenChange={setShowPhotoUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Photo
            </DialogTitle>
            <DialogDescription>
              Upload an image (JPEG, PNG, WebP, or GIF, max 5MB)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handlePhotoUpload}
              disabled={formLoading}
              data-testid="photo-upload-input"
            />
            {formLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoUpload(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
